import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Portal,
} from '@headlessui/react';
import { EllipsisVerticalIcon, LinkSlashIcon } from '@heroicons/react/24/solid';
import React from 'react';

import { Attribute } from '@/types';
import { cn } from '@/utils/classname';

export interface JiraTicketActionsMenuProps {
  /** The Jira ticket attribute */
  ticket: Attribute;
  /** Callback when disconnect action is triggered */
  onDisconnect: () => void;
  /** Optional test id prefix */
  testIdPrefix?: string;
}

/**
 * Three-dot actions menu for Jira tickets in the Related Tickets section.
 *
 * Provides:
 * - Disconnect action (LinkSlashIcon) with danger variant
 *
 * Usage:
 * ```tsx
 * <JiraTicketActionsMenu
 *   ticket={ticket}
 *   onDisconnect={handleDisconnect}
 * />
 * ```
 */
export function JiraTicketActionsMenu({
  ticket,
  onDisconnect,
  testIdPrefix = 'jira-ticket',
}: JiraTicketActionsMenuProps) {
  return (
    <Menu as="div" className="relative">
      <MenuButton
        className={cn(
          'rounded-full p-1.5 transition-colors',
          'bg-chariot-background-secondary/80 shadow-sm',
          'hover:bg-chariot-background-secondary',
          'focus:ring-chariot-brand-primary focus:ring-2 focus:outline-none'
        )}
        data-testid={`${testIdPrefix}-actions-${ticket.key}`}
        aria-label={`Actions for Jira ticket ${ticket.value}`}
        onClick={e => e.stopPropagation()}
      >
        <EllipsisVerticalIcon className="text-chariot-text-secondary size-4" />
      </MenuButton>

      <Portal>
        <MenuItems
          anchor="bottom end"
          className={cn(
            'z-50 mt-1 w-40',
            'bg-chariot-background-primary rounded-lg shadow-lg',
            'border-chariot-stroke border',
            'focus:outline-none'
          )}
        >
          <MenuItem>
            {({ focus }) => (
              <button
                onClick={e => {
                  e.stopPropagation();
                  onDisconnect();
                }}
                className={cn(
                  'flex w-full items-center gap-2 px-4 py-2 text-left text-sm',
                  focus && 'bg-chariot-background-secondary',
                  'text-chariot-stroke-critical'
                )}
                data-testid={`${testIdPrefix}-actions-${ticket.key}-disconnect`}
              >
                <LinkSlashIcon className="size-4" />
                Disconnect
              </button>
            )}
          </MenuItem>
        </MenuItems>
      </Portal>
    </Menu>
  );
}
